import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Gauge, Settings2, Save, Radio } from "lucide-react";
import { toast } from "sonner";

const eur = (v, d = 2) => `€${Number(v || 0).toFixed(d).replace(".", ",")}`;
const FIELDS = [
  ["credit_eur", "Valore di 1 credito (€)"], ["credits_ai", "Crediti per elaborazione"],
  ["credits_ai_pdf", "Crediti per lettura PDF"], ["credits_email", "Crediti per email"],
  ["monthly_fixed_credits", "Crediti fissi al mese (pubblicazione)"],
];

const Kpi = ({ label, value, sub, testid, accent }) => (
  <div className={`rounded-2xl p-4 ${accent ? "bg-brand text-white" : "bg-secondary/50"}`} data-testid={testid}>
    <div className={`text-xs ${accent ? "text-white/80" : "text-muted-foreground"}`}>{label}</div>
    <div className="font-mono-data text-2xl font-semibold mt-1">{value}</div>
    {sub && <div className={`text-[11px] mt-0.5 ${accent ? "text-white/75" : "text-muted-foreground"}`}>{sub}</div>}
  </div>
);

const ConfigForm = ({ cfg, onSaved }) => {
  const [form, setForm] = useState(cfg);
  const save = async () => {
    try {
      await api.put("/admin/usage/config", Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)])));
      toast.success("Costi unitari aggiornati"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid="usage-config">
      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
            <Input type="number" step="0.01" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`usage-config-${k}`} /></div>
        ))}
      </div>
      <Button size="sm" onClick={save} data-testid="usage-config-save"><Save className="h-4 w-4 mr-1" /> Salva costi</Button>
    </div>
  );
};

export const UsagePanel = () => {
  const [d, setD] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const load = () => api.get("/admin/usage").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!d) return null;
  const a = d.averages;

  return (
    <Card className="rounded-2xl" data-testid="usage-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Gauge className="h-5 w-5 text-primary" /> Consumi e costi</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1 text-primary border-primary/30"><Radio className="h-3 w-3 animate-pulse" /> In tempo reale</Badge>
          <Button size="sm" variant="outline" onClick={() => setShowCfg((v) => !v)} data-testid="usage-config-toggle"><Settings2 className="h-4 w-4 mr-1" /> Costi unitari</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {showCfg && <ConfigForm cfg={d.config} onSaved={load} />}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi accent label="Costo questo mese" value={eur(d.month.eur)} sub={`${eur(d.month.variable_eur)} uso + ${eur(d.month.fixed_eur)} fisso`} testid="usage-kpi-month" />
          <Kpi label="Costo oggi" value={eur(d.today.eur)} sub={`${d.today.ai_calls} elaborazioni · ${d.today.emails} email`} testid="usage-kpi-today" />
          <Kpi label="Costo medio per utente (mese)" value={eur(a.per_user_eur, 3)} sub={`${a.total_users} utenti registrati`} testid="usage-kpi-per-user" />
          <Kpi label="Margine stimato (mese)" value={eur(d.margin_month_eur)} sub={`Incasso ${eur(d.revenue_month_eur)}`} testid="usage-kpi-margin" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Media utente attivo" value={eur(a.per_active_user_eur, 3)} sub={`${a.active_users} utenti con consumi`} testid="usage-avg-active" />
          <Kpi label="Media utente gratuito" value={eur(a.free_eur, 3)} testid="usage-avg-free" />
          <Kpi label="Media utente Premium" value={eur(a.premium_eur, 3)} sub={`${a.premium_users} Premium`} testid="usage-avg-premium" />
          <Kpi label="Totale uso dall'inizio" value={eur(d.total.variable_eur)} sub={`${d.total.ai_calls} elaborazioni · ${d.total.emails} email`} testid="usage-total" />
        </div>

        <div className="h-56" data-testid="usage-daily-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.daily} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" interval={2} />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => eur(v, 2)} />
              <Tooltip formatter={(v) => eur(v, 3)} labelFormatter={(l) => `Giorno ${l}`}
                contentStyle={{ borderRadius: 12, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Bar dataKey="eur" name="Costo" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-2" data-testid="usage-by-operation">
            <div className="text-sm font-semibold">Costo per tipo di operazione (mese)</div>
            {d.by_operation.length === 0 && <p className="text-xs text-muted-foreground">Nessun consumo registrato questo mese.</p>}
            {d.by_operation.map((o) => {
              const max = d.by_operation[0]?.eur || 1;
              return (
                <div key={o.op} className="space-y-1">
                  <div className="flex justify-between text-xs"><span>{o.op}</span><span className="font-mono-data">{eur(o.eur, 3)} · {o.ai_calls + o.emails}</span></div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-brand" style={{ width: `${Math.max(4, (o.eur / max) * 100)}%` }} /></div>
                </div>
              );
            })}
          </div>
          <div className="space-y-2" data-testid="usage-by-user">
            <div className="text-sm font-semibold flex justify-between"><span>Costo per utente (mese)</span><span className="text-xs text-muted-foreground font-normal">Automatismi senza utente: {eur(d.system_eur, 3)}</span></div>
            <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
              {d.by_user.length === 0 && <p className="text-xs text-muted-foreground">Nessun consumo registrato questo mese.</p>}
              {d.by_user.map((u, i) => (
                <div key={u.user_id} className="flex items-center justify-between gap-2 rounded-lg bg-secondary/40 px-3 py-2 text-xs" data-testid={`usage-user-${u.email || u.user_id}`}>
                  <span className="min-w-0 truncate"><span className="text-muted-foreground mr-1">{i + 1}.</span>{u.name} <span className="text-muted-foreground">{u.email}</span>
                    {u.premium && <Badge variant="outline" className="ml-1 text-[9px] py-0">Premium</Badge>}</span>
                  <span className="font-mono-data shrink-0">{eur(u.eur, 3)} · {u.ai_calls} el. · {u.emails} em.</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Stima basata sulle operazioni registrate{d.tracking_since ? ` dal ${new Date(d.tracking_since).toLocaleDateString("it-IT")}` : ""} e sui costi unitari impostati; il saldo crediti reale resta quello del tuo account Emergent. Aggiornamento automatico ogni 30 secondi.
        </p>
      </CardContent>
    </Card>
  );
};
